import React from 'react'
import { Meta, Title } from 'react-head'
import DefaultLayout from 'js/components/DefaultLayout'
import Footer from 'js/components/Footer/Footer'
import { runQuery } from 'js/dato'
import withSSR from 'js/components/withSSR/withSSR'
import ErrorPage from '../ErrorPage'
import Spinner from 'js/components/Spinner/Spinner'
import { BLOG_POSTS_QUERY } from './queries/blogPostsQuery'
import { BLOG_UI_TEXT_QUERY } from './queries/blogUITextQuery'
import type { BlogPostsResponse } from './types'
import useLocale from 'js/hooks/useLocale'
import PostInList from './components/PostInList'
import './blog.css'
import BlogHero from './components/BlogHero'

type Props = {
  data: {
    allBlogPosts: BlogPostsResponse['allBlogPosts'] | null
    _allBlogPostsMeta: BlogPostsResponse['_allBlogPostsMeta'] | null
    uiTexts: Array<{ slug: string; text: string }>
  } | null
  isLoading: boolean
  error: Error | null
}

const loadingStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '50vh',
}

function BlogListPage(props: Props) {
  const { language } = useLocale()
  const { data, isLoading, error } = props
  const { allBlogPosts, uiTexts } = data || {}

  if (isLoading) {
    return (
      <DefaultLayout>
        <div style={loadingStyles}>
          <Spinner width="50" color="green" />
        </div>
      </DefaultLayout>
    )
  }

  if (error) {
    return <ErrorPage error={error} />
  }

  const [heroPost, ...restPosts] = allBlogPosts || []
  const heroTitle = heroPost?.title || ''
  const heroDescription = heroPost?.seo?.description || ''
  const heroUrl = heroPost ? `/${language}/blog/${heroPost.slug}` : undefined

  return (
    <DefaultLayout>
      <Title>Blog - Lifesum</Title>
      <Meta name="description" content="Latest blog posts from Lifesum" />
      <BlogHero
        title={heroTitle}
        description={heroDescription}
        buttonLabel={
          uiTexts?.find((text) => text.slug === 'read-more')?.text ||
          'READ MORE'
        }
        url={heroUrl}
      />
      <div className="blog-container">
        <div className="blog-list">
          {restPosts?.map((post) => <PostInList key={post.id} post={post} />)}
        </div>
      </div>
      <Footer />
    </DefaultLayout>
  )
}

BlogListPage.getInitialProps = async ({ language }) => {
  const locale = language || 'en'

  // Try to fetch posts and UI texts in the requested language
  let [postsData, uiTextData] = await Promise.all([
    runQuery(BLOG_POSTS_QUERY, { locale }),
    runQuery(BLOG_UI_TEXT_QUERY, { locale }),
  ])

  // If no posts found, fallback to English
  if (!postsData.allBlogPosts || postsData.allBlogPosts.length === 0) {
    ;[postsData, uiTextData] = await Promise.all([
      runQuery(BLOG_POSTS_QUERY, { locale: 'en' }),
      runQuery(BLOG_UI_TEXT_QUERY, { locale: 'en' }),
    ])
  }

  return {
    ...postsData,
    uiTexts: uiTextData?.allBlogUiTexts || [],
  }
}

export default withSSR(BlogListPage)
